import { z } from "zod";
import { QRScanner } from "./qr-scanner";
import { Box, Button, Dialog, Flex, Heading, IconButton, Spinner, Text } from "@radix-ui/themes";
import { Cross1Icon } from "@radix-ui/react-icons";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { twMerge } from "tailwind-merge";
import Countdown from "react-countdown";
import { useMobileCheckoutMutation } from "../../api/pos-api";
import { QueryStatus } from "@reduxjs/toolkit/query";
import { AxiosError } from "axios";
import { RequestError } from "../../api/types";

export const appQRSchema = z.object({
  user_id: z.string(),
  user_type: z.string(),
  token: z.string(),
  timestamp: z.string(),
  card_id: z.string(),
  email: z.string(),
});

export function MobileAppCheckout() {
  const { zoneId = "" } = useParams();
  const [open, setOpen] = useState(false);
  const [makePayment, { status, error }] = useMobileCheckoutMutation();

  const { t } = useTranslation(undefined, { lng: "tr" });

  const onScan = async (value: z.infer<typeof appQRSchema>) => {
    setOpen(true);
    makePayment({
      ...value,
      zone_id: zoneId,
    });
  };

  return (
    <>
      <div className="bg-tezzro-bg p-8 h-screen">
        <div className="relative bg-white border rounded-2xl border-card-border w-full h-full flex items-center justify-center">
          <div className="fixed top-0 left-[12%] bg-white border rounded-b-lg p-2 shadow-downloadCard">
            <Text align="center" as="p" size="1" color="indigo" weight="bold">
              {t("downloadTheApp")}
            </Text>
            <img width={120} height={120} src="/mobile-app-download-qr.svg" />
          </div>

          <div className="h-full">
            <div className="min-w-[400px] h-full">
              <div className="flex flex-col justify-center items-center h-full">
                <figure className="mb-6">
                  <img src="/qr-scan-mobile-app.jpg" className="object-contain max-w-[652px]" />
                </figure>
                <Text size="8" weight="bold" mb="4">
                  {t("welcomeAutonomous")}{" "}
                  <span className="inline-block animate-wave origin-[70%_70%] align-bottom">
                    <img className="w-[40px] h-[40px]" src="/waving-hand.png" />
                  </span>
                </Text>
                <div className="flex gap-4">
                  <Text size="6" weight="bold">
                    {t("scanQRCode")}
                  </Text>
                </div>
                <QRScanner<z.infer<typeof appQRSchema>> onScanned={onScan} schema={appQRSchema} />
              </div>
            </div>
          </div>
        </div>
      </div>
      <CheckoutModal
        open={open}
        status={status}
        error={error as AxiosError<RequestError>["response"] | undefined}
        setOpen={setOpen}
      />
    </>
  );
}

function CheckoutModal({
  open,
  status,
  error,
  setOpen,
}: {
  open: boolean;
  status: QueryStatus;
  error: AxiosError<RequestError>["response"] | undefined;
  setOpen: (value: boolean) => void;
}) {
  return (
    <Dialog.Root open={open}>
      <Dialog.Content style={{ maxWidth: 450, borderRadius: 16 }}>
        {status === QueryStatus.fulfilled ? (
          <CheckoutSuccessful
            onPrintReceipt={console.log}
            onClose={() => {
              setOpen(false);
            }}
          />
        ) : status === QueryStatus.rejected ? (
          <CheckoutFailed
            error={error}
            onRetry={() => {
              setOpen(false);
            }}
            onClose={() => {
              setOpen(false);
            }}
          />
        ) : (
          <CheckoutPending />
        )}
      </Dialog.Content>
    </Dialog.Root>
  );
}

export function CheckoutSuccessful({ onClose }: { onPrintReceipt?: () => void; onClose: () => void }) {
  const [closeDate] = useState(() => Date.now() + 5000);
  const isCloseLoading = false;
  const { t } = useTranslation(undefined, { lng: "tr" });

  return (
    <div className="flex flex-col w-full items-center self-stretch p-8">
      <div className="flex-grow">
        <figure className="flex justify-center">
          <img src="/success.png" className="w-[100px] h-[100px]" />
        </figure>
        <Heading size="6" mb="2" align="center" weight="bold" color="gray" highContrast>
          {t("checkoutCompleted")}
        </Heading>
      </div>
      <Flex className="w-full" direction="column" justify="between" gap="2" mt="4">
        <Button className="w-full !relative !overflow-hidden" size="4" onClick={onClose}>
          <Spinner loading={isCloseLoading} size="2"></Spinner>
          <span className="relative z-20">{!isCloseLoading ? t("closeCheckout") : t("closingCheckout")}</span>
          <span
            className={twMerge([
              "h-full !absolute top-0 left-0 bg-black/20 animate-payment-close transition-opacity",
              isCloseLoading && "opacity-0",
            ])}
          ></span>
        </Button>
        <Countdown
          date={closeDate}
          onComplete={onClose}
          renderer={({ seconds }) => (
            <Text color="gray" align="center">
              {t("autoClosingCheckoutInNSeconds", { nSeconds: seconds })}
            </Text>
          )}
        ></Countdown>
      </Flex>
    </div>
  );
}

export function CheckoutPending() {
  const { t } = useTranslation(undefined, { lng: "tr" });

  return (
    <Box className="w-full h-full flex flex-col items-center justify-between p-2 py-12">
      <figure className="flex justify-center mb-4 overflow-hidden">
        <video
          autoPlay
          playsInline
          muted
          loop
          className="scale-150"
          src="/phone-payment-processing-600px.webm"
        />
      </figure>
      <Flex mt="2" gap="4" justify="center" align="center">
        <Heading align="center" size="6" weight="bold" color="gray" highContrast>
          {t("paymentProcessing")}
        </Heading>
      </Flex>
    </Box>
  );
}

export function CheckoutFailed({
  error,
  onRetry,
  onClose,
}: {
  error: AxiosError<RequestError>["response"] | undefined;
  onRetry: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation(undefined, { lng: "tr" });

  return (
    <Box className="w-full p-8">
      <Dialog.Close className="!absolute !top-5 !right-5">
        <IconButton variant="ghost" color="gray" highContrast size="3" onClick={onClose}>
          <Cross1Icon width={18} height={18} />
        </IconButton>
      </Dialog.Close>
      <Flex justify="between" align="center" direction="column" className="w-full h-full">
        <figure className="flex justify-center">
          <img src="/error.png" className="w-[100px] h-[100px]" />
        </figure>
        <Heading size="6" mb="2" weight="bold" color="gray" highContrast>
          {t("paymentFailed")}
        </Heading>
        <Flex direction="column" gap="3" mb="6">
          <Text as="p" size="3" align="center" color="gray">
            {error?.data?.errors[0]?.msg || t("paymentFailedDescription")}
          </Text>
        </Flex>
        <Button size="4" className="w-[100%!important]" variant="solid" onClick={onRetry}>
          {t("tryAgain")}
        </Button>
      </Flex>
    </Box>
  );
}
